import axios, { AxiosError } from "axios";
import { EMPLOYEE_TRANSFER_API_PATHS } from "@/features/employeeTransfer/config/employeeTransferApiPath";
import type {
  TransferInfoResponse,
  OtpRequestResponse,
  OtpVerifyPayload,
  OtpVerifyResponse,
  RefuseResponse,
} from "@/features/employeeTransfer/types/employeeTransfer.types";
import { getApiBaseUrl } from "@/shared/utils/api.util";

const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || "30000");

const anonymousFetcher = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: API_TIMEOUT,
  headers: { "Content-Type": "application/json", "X-Client-Type": "web" },
});

export const validateTransfer = async (token: string, signal?: AbortSignal): Promise<TransferInfoResponse> => {
  const response = await anonymousFetcher.get<TransferInfoResponse>(
    EMPLOYEE_TRANSFER_API_PATHS.validate(token),
    { signal },
  );
  return response.data;
};

export const requestOtp = async (token: string): Promise<OtpRequestResponse> => {
  const response = await anonymousFetcher.post<OtpRequestResponse>(
    EMPLOYEE_TRANSFER_API_PATHS.requestOtp(token),
  );
  return response.data;
};

export const verifyOtp = async (token: string, payload: OtpVerifyPayload): Promise<OtpVerifyResponse> => {
  const response = await anonymousFetcher.post<OtpVerifyResponse>(
    EMPLOYEE_TRANSFER_API_PATHS.verifyOtp(token),
    payload,
  );
  return response.data;
};

export const refuseTransfer = async (token: string, payload: OtpVerifyPayload): Promise<RefuseResponse> => {
  const response = await anonymousFetcher.post<RefuseResponse>(
    EMPLOYEE_TRANSFER_API_PATHS.refuse(token),
    payload,
  );
  return response.data;
};

export const requestResend = async (token: string): Promise<{ success: true, message: string }> => {
  const response = await anonymousFetcher.post<{ success: true, message: string }>(
    EMPLOYEE_TRANSFER_API_PATHS.requestResend(token),
  );
  return response.data;
};

export const extractErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const data = error.response?.data;
    return data?.error ?? "Une erreur est survenue. Veuillez réessayer.";
  }
  return "Une erreur est survenue. Veuillez réessayer.";
};

export const extractErrorStatus = (error: unknown): number | undefined => {
  if (error instanceof AxiosError) {
    return error.response?.status;
  }
  return undefined;
};

export const extractErrorResendable = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.data?.resendable === true;
  }
  return false;
};

export const extractErrorKind = (error: unknown): string | undefined => {
  if (error instanceof AxiosError) {
    return error.response?.data?.error_kind;
  }
  return undefined;
};
